import { useMemo } from 'react'
import isNumber from 'lodash/isNumber'
import toInteger from 'lodash/toInteger'
import type { Payable } from '@klear/payable/payable'
import { paymentFromPayable } from '@klear/payable/payment-from-payable'
import { Payment, type OrderEventForPayment, type PaymentJson } from '@klear/payment/payment'
import {
  PAYMENT_ACCOUNT_TYPE_EXTERNAL_FINANCIAL_ACCOUNT,
  PAYMENT_ACCOUNT_TYPE_KLEAR_FINANCIAL_ACCOUNT
} from '@klear/payment/constants'
import { PaymentAccount, type PaymentAccountOrganization } from '@klear/payment/payment-account'
import { operationalSupplierIdAsNumber } from '@klear/operational'
import type { SupplierExternalBankAccount } from '@klear/supplier-external-bank-account'
import type { OrderEventRef } from '@klear/ui/hooks/order-event-ref'
import { useKlearFinancialAccount } from '@klear/ui/hooks/use-klear-financial-account'
import { useSupplierExternalBankAccounts } from '@klear/ui/hooks/use-supplier-external-bank-accounts'
import { useSupplier } from '@klear/ui/hooks/use-suppliers'
import { trpc } from '@klear/ui/lib/trpc'

function parseSupplierId(value: unknown): number | undefined {
  if (value == null) return undefined
  if (isNumber(value)) return value > 0 ? value : undefined
  const parsed = toInteger(value)
  return parsed > 0 ? parsed : undefined
}

function findSupplierExternalAccount(
  accounts: SupplierExternalBankAccount[],
  accountId: number | undefined
): SupplierExternalBankAccount | undefined {
  if (accountId == null) return undefined
  return accounts.find((account) => account.id === accountId)
}

function supplierExternalToPaymentAccount(
  account: SupplierExternalBankAccount | undefined,
  organization: PaymentAccountOrganization | undefined
): PaymentAccount | null {
  if (!account || !organization) return null
  return PaymentAccount.fromSupplierExternalBankAccount(account, organization)
}

function paymentAsDomain(payment: Payment | PaymentJson): Payment {
  return payment instanceof Payment ? payment : new Payment(payment)
}

export function orderEventForPaymentFromPayable(payable: Payable): OrderEventForPayment {
  return paymentFromPayable(payable).toOrderEventForPayment()
}

export function orderEventForPaymentFromPayment(
  payment: Payment | PaymentJson,
  options?: { orderEventId?: number }
): OrderEventForPayment {
  return paymentAsDomain(payment).toOrderEventForPayment(options)
}

export function usePaymentPreviewAccounts(
  event: OrderEventForPayment | null | undefined,
  supplierId: number | undefined,
  enabled: boolean
) {
  const resolvedSupplierId = useMemo(() => parseSupplierId(supplierId), [supplierId])
  const { accounts, receivableAccount, isLoading: isKlearLoading } = useKlearFinancialAccount({ enabled })

  const sourceKlearAccount = useMemo(() => {
    if (event?.sourceAccountType !== PAYMENT_ACCOUNT_TYPE_KLEAR_FINANCIAL_ACCOUNT) return null
    if (event.sourceAccountId != null) {
      return accounts.find((a) => a.id === event.sourceAccountId) ?? null
    }
    return receivableAccount ?? accounts[0] ?? null
  }, [accounts, receivableAccount, event])

  const destinationKlearAccount = useMemo(() => {
    if (event?.destinationAccountType !== PAYMENT_ACCOUNT_TYPE_KLEAR_FINANCIAL_ACCOUNT) return null
    if (event.destinationAccountId == null) return null
    return accounts.find((a) => a.id === event.destinationAccountId) ?? null
  }, [accounts, event])

  const { supplierExternalBankAccounts, isLoading: isExternalListLoading } = useSupplierExternalBankAccounts(
    enabled ? resolvedSupplierId : undefined
  )
  const { supplier, isLoading: isSupplierLoading } = useSupplier((enabled && resolvedSupplierId) || 0)
  const supplierOrganization = useMemo<PaymentAccountOrganization | undefined>(
    () =>
      supplier?.organizationId != null
        ? { id: supplier.organizationId, name: supplier.name, stripeAccountId: '' }
        : undefined,
    [supplier?.organizationId, supplier?.name]
  )

  const destinationExternal = useMemo(
    () => (event ? findSupplierExternalAccount(supplierExternalBankAccounts, event.destinationAccountId) : undefined),
    [supplierExternalBankAccounts, event]
  )

  const sourceAccount = useMemo((): PaymentAccount | null => {
    if (!enabled || !event) return null
    if (event.sourceAccountType === PAYMENT_ACCOUNT_TYPE_KLEAR_FINANCIAL_ACCOUNT && sourceKlearAccount) {
      return PaymentAccount.fromKlearFinancialAccount(sourceKlearAccount)
    }
    if (event.sourceAccountType === PAYMENT_ACCOUNT_TYPE_EXTERNAL_FINANCIAL_ACCOUNT) {
      const external = findSupplierExternalAccount(supplierExternalBankAccounts, event.sourceAccountId)
      return supplierExternalToPaymentAccount(external, supplierOrganization)
    }
    return null
  }, [enabled, event, supplierExternalBankAccounts, sourceKlearAccount, supplierOrganization])

  const destinationAccount = useMemo((): PaymentAccount | null => {
    if (!enabled || !event) return null
    if (event.destinationAccountType === PAYMENT_ACCOUNT_TYPE_EXTERNAL_FINANCIAL_ACCOUNT) {
      return supplierExternalToPaymentAccount(destinationExternal, supplierOrganization)
    }
    if (event.destinationAccountType === PAYMENT_ACCOUNT_TYPE_KLEAR_FINANCIAL_ACCOUNT && destinationKlearAccount) {
      return PaymentAccount.fromKlearFinancialAccount(destinationKlearAccount)
    }
    return null
  }, [enabled, destinationExternal, event, destinationKlearAccount, supplierOrganization])

  const payment = useMemo(() => (event ? Payment.fromOrderEvent(event) : null), [event])

  const isLoading = isKlearLoading || isExternalListLoading || isSupplierLoading

  return { payment, sourceAccount, destinationAccount, isLoading }
}

export function usePaymentPreviewForComplete(
  eventRef: OrderEventRef,
  parentEventId: number | undefined,
  enabled: boolean
) {
  const paymentQuery = trpc.payable.getPaymentForOrderEvent.useQuery(
    { orderEventId: eventRef.id, traderId: eventRef.traderId },
    { enabled: enabled && eventRef.id > 0 }
  )
  const operationalQuery = trpc.operational.get.useQuery(
    { id: parentEventId ?? 0, traderId: eventRef.traderId },
    { enabled: enabled && parentEventId != null && parentEventId > 0 }
  )

  const paymentSource = useMemo(
    () =>
      enabled && paymentQuery.data
        ? orderEventForPaymentFromPayment(paymentQuery.data, { orderEventId: eventRef.id })
        : null,
    [enabled, eventRef.id, paymentQuery.data]
  )
  const supplierId = operationalSupplierIdAsNumber(operationalQuery.data?.supplierId)

  const preview = usePaymentPreviewAccounts(paymentSource, supplierId, enabled && paymentSource != null)

  const isLoading =
    preview.isLoading ||
    (enabled && paymentQuery.isFetching) ||
    (enabled && parentEventId != null && parentEventId > 0 && operationalQuery.isFetching)

  return { ...preview, isLoading }
}
